import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { TabContext } from '@mui/lab';
import { Grid } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { cloneDeep, get } from 'lodash';
import { useEffect, useState } from 'react';
import { type FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { Drawer } from 'src/components/drawer';
import { ColorButtonGroup } from 'src/components/form/colorButtonGroup/ColorButtonGroup';
import { SidebarHeader } from 'src/components/header';
import { SwitchButtonWidget } from 'src/components/widget';
import { injectDefaultFontIntoHtml } from 'src/components/widget/richTextWidget';
import { COD_COMPONENT_CONFIG } from 'src/constants/gcfComponentConfig/gcfCodConfig';
import { useGetProjectConfig } from 'src/hooks';
import { lang } from 'src/utils';
import { GcfMockData } from 'src/utils/gcf-helper';

import {
  hasEmptyMessage,
  isNonEmptyString,
  sanitizeRichTextInObject,
} from '../../EditComponentPage.helper';
import { CustomerTypes } from '../codThankyou/CODThankYouForm.interface';
import { FormWrapper } from '../FormWrapper';
import {
  SidebarContainer,
  SidebarContent,
} from '../singleShelf/SingleShelfForm.styled';
import { useCODForm } from './CODForm.hook';
import {
  type IMessageConfig,
  BackgroundColor,
  CodDataInterface,
  CODFormProps,
  DividerColor,
  HandleOnChangeType,
  MessageConfigTypes,
  PaymentTypes,
  TAB_VALUES,
} from './CODForm.interface';
import { CodSchema } from './CODForm.schema';
import {
  ButtonContainer,
  CustomTab,
  CustomTabList,
  CustomTabPanel,
} from './CODForm.styled';
import { CustomCodSettings } from './componentSettings';
import { ConfirmOrderMessages } from './confirmOrderMessages/ConfirmOrderMessages';
import { HeaderSection } from './headerSection';
import { PaymentTypeToggleButton } from './headerSection/modules/PaymentTypeToggleButton';
import { MessagingAccordion } from './messagingAccordion';
import { EyeIconButton } from './messagingTitle/eyeIconButton/EyeIconButton';
import { OrderConfirmTitle } from './orderConfirmTitle';
import { TitleAccordion } from './titleAccordion';

export const CODForm = (props: CODFormProps) => {
  const [paymentEventType, setPaymentEventType] = useState<PaymentTypes>(
    PaymentTypes.UNLINKED_PAYMENT,
  );
  const [selectedCustomer, setSelectedCustomer] = useState(
    CustomerTypes.UNKNOWN,
  );

  const {
    selectedTab,
    handleTabChange,
    openBackgroundDrawer,
    openTitleDrawer,
    openCustomMessageDrawer,
    openHeaderSectionDrawer,
    openConfirmTitleDrawer,
    openKnownUserDrawer,
    openUnknownUserDrawer,
    openComponentSettingsDrawer,
    onComponentSettingsDrawer,
    onHeaderSectionDrawer,
    onToggleBackgroundDrawer,
    onToggleTitleDrawer,
    onCustomMessageDrawer,
    onToggleConfirmTitleDrawer,
    onToggleKnownUserDrawer,
    onToggleUnknownUserDrawer,
    onBackgroundColorChange,
  } = useCODForm();

  const generateCODMessage = (customer: string): IMessageConfig => {
    return {
      ...COD_COMPONENT_CONFIG.codSettings.confirmSetting.footer
        .unknownCustomer[0],
      image: '',
      iconAndText: {
        ...COD_COMPONENT_CONFIG.codSettings.confirmSetting.footer
          .unknownCustomer[0].iconAndText,
        icon: '',
      },
      ...(customer === CustomerTypes.KNOWN && {
        wrapperStyle:
          COD_COMPONENT_CONFIG.codSettings.confirmSetting.footer.knownCustomer
            .linkedPayment[0].wrapperStyle,
      }),
    };
  };

  const { handleSubmit: submitWrapper, formState } = useForm<CodDataInterface>({
    values: cloneDeep(props.componentDetails.config as CodDataInterface),
    resolver: ajvResolver(
      CodSchema as unknown as JSONSchemaType<CodDataInterface>,
    ),
  });

  const showHeaderSection = get(
    props.componentDetails,
    'config.codSettings.confirmSetting.header.review.banner.active',
    true,
  );

  const showCodSettingRmhcMessage = get(
    props.componentDetails,
    'config.codSettings.codSetting.topMessage.hideRmhcMessage',
    false,
  );

  const TAB_VALUES = {
    COD: 'COD',
    OCD: 'OCD',
  };

  const handleSubmit: SubmitHandler<CodDataInterface> = (value: any) => {
    props.onSave(value);
  };

  const handleOnChange: HandleOnChangeType = (key, value) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
  };

  const { data: projectConfig } = useGetProjectConfig();
  const activeThemeKey = projectConfig?.activeTheme;
  const activeTheme = activeThemeKey
    ? projectConfig?.themes?.[activeThemeKey]
    : undefined;
  const componentTheme = props.componentDetails.theme?.config;
  const rteDefaultStyles = {
    fontFamily: componentTheme?.font ?? activeTheme?.font,
    color: componentTheme?.color ?? activeTheme?.color,
  };

  useEffect(() => {
    if (!rteDefaultStyles.fontFamily) return;

    const changedPaths: string[] = [];
    const changedValues: string[] = [];

    const collectIfChanged = (path: string) => {
      const message = get(props.componentDetails, `config.${path}`, '');
      if (!message) return;
      // Seed font only — never inject color, so the message inherits the
      // section's container color via the GCF cascade (see CC-22307).
      const styled = injectDefaultFontIntoHtml(message, {
        fontFamily: rteDefaultStyles.fontFamily,
      });
      if (styled !== message) {
        changedPaths.push(path);
        changedValues.push(styled);
      }
    };

    // Customer order tab
    collectIfChanged('codSettings.codSetting.topMessage.message');
    collectIfChanged(
      `codSettings.codSetting.footer.${CustomerTypes.KNOWN}.${PaymentTypes.LINKED_PAYMENT}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`,
    );
    collectIfChanged(
      `codSettings.codSetting.footer.${CustomerTypes.KNOWN}.${PaymentTypes.UNLINKED_PAYMENT}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`,
    );
    collectIfChanged(
      `codSettings.codSetting.footer.${CustomerTypes.UNKNOWN}.0.${MessageConfigTypes.ICON_AND_TEXT}.label`,
    );

    // Order confirmation tab
    collectIfChanged('codSettings.confirmSetting.topMessage.message');

    const confirmMessageArrayPaths = [
      `codSettings.confirmSetting.footer.${CustomerTypes.KNOWN}.${PaymentTypes.LINKED_PAYMENT}`,
      `codSettings.confirmSetting.footer.${CustomerTypes.KNOWN}.${PaymentTypes.UNLINKED_PAYMENT}`,
      `codSettings.confirmSetting.footer.${CustomerTypes.UNKNOWN}`,
    ];

    confirmMessageArrayPaths.forEach((basePath) => {
      const messages: IMessageConfig[] = get(
        props.componentDetails,
        `config.${basePath}`,
        [],
      );
      messages.forEach((msg, index) => {
        if (msg.iconAndText?.label) {
          collectIfChanged(
            `${basePath}.${index}.${MessageConfigTypes.ICON_AND_TEXT}.label`,
          );
        }
      });
    });

    if (changedPaths.length > 0) {
      handleOnChange(changedPaths, changedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rteDefaultStyles.fontFamily]);

  useEffect(() => {
    if (!rteDefaultStyles.color) return;
    const path = 'codSettings.globalSetting.color';
    const current = get(props.componentDetails, `config.${path}`, '');
    // Only seed when no section color is configured. This sets the theme base
    // color on the COD container so colorless messages inherit it via the GCF
    // root cascade.
    if (current) return;
    handleOnChange(path, rteDefaultStyles.color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rteDefaultStyles.color]);

  const tabChange = (event: React.SyntheticEvent, newValue: TAB_VALUES) => {
    handleTabChange(event, newValue);
    if (newValue === 'OCD') {
      handleSelectedCustomer(CustomerTypes.UNKNOWN)(event as any);

      props.setSize({
        height: 1920,
        width: 1080,
      });
    } else {
      props.setSize(undefined);
    }
  };

  const handleSelectedCustomer =
    (customer: CustomerTypes) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSelectedCustomer(customer);
      const orderType =
        customer === CustomerTypes.KNOWN
          ? paymentEventType === PaymentTypes.LINKED_PAYMENT
            ? GcfMockData.COD_CUSTOMER_KNOWN_MOP
            : GcfMockData.COD_CUSTOMER_KNOWN_UNLINKED_PAYMENT
          : GcfMockData.COD_CUSTOMER_UNKNOWN;

      props.setOrderType(orderType);
    };

  const handleChangePaymentEventType = (
    selectedPaymentEventType: PaymentTypes,
  ) => {
    setPaymentEventType(selectedPaymentEventType);

    if (selectedCustomer === CustomerTypes.KNOWN) {
      const orderType =
        selectedPaymentEventType === PaymentTypes.LINKED_PAYMENT
          ? GcfMockData.COD_CUSTOMER_KNOWN_MOP
          : GcfMockData.COD_CUSTOMER_KNOWN_UNLINKED_PAYMENT;

      props.setOrderType(orderType);
    }
  };

  const hasCodFullBanner = isNonEmptyString(
    props?.componentDetails?.config?.codSettings?.confirmSetting?.header?.review
      ?.banner?.images?.largeImage?.background,
  );
  const hasCodHalfBanner = isNonEmptyString(
    props?.componentDetails?.config?.codSettings?.confirmSetting?.header?.review
      ?.banner?.images?.smallImage?.background,
  );

  const hasConfirmationDonationLabel = isNonEmptyString(
    get(
      props,
      'componentDetails.config.codSettings.confirmSetting.total.donate.label',
      '',
    ) as string,
  );

  const items = {
    customerOrder: [
      {
        label: lang.codColorDrawer,
        onLabelClick: onToggleBackgroundDrawer,
        content: <div />,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError: false,
      },
      {
        label: lang.headerTextAndStyling,
        onLabelClick: onToggleTitleDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError:
          !!formState.errors.codSettings?.codSetting ||
          (hasEmptyMessage(
            sanitizeRichTextInObject(
              props?.componentDetails?.config?.codSettings?.codSetting,
            ),
          ) &&
            props.hasTriedSaving),
        content: <div></div>,
      },
      {
        label: lang.codCustomMessaging,
        onLabelClick: onCustomMessageDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError: !!formState.errors?.codSettings?.codSetting?.footer,
        content: <div></div>,
      },
      {
        label: lang.componentSettings,
        onLabelClick: onComponentSettingsDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError: !!formState.errors?.codSettings?.codSetting?.footer,
        content: <div />,
      },
    ],
    orderConfirmation: [
      {
        label: lang.codHeaderSection,
        onLabelClick: onHeaderSectionDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError:
          !!formState.errors.codSettings?.confirmSetting?.header ||
          (props.hasTriedSaving && (!hasCodFullBanner || !hasCodHalfBanner)),
        content: <div />,
      },
      {
        label: lang.headerTextAndStyling,
        onLabelClick: onToggleConfirmTitleDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError:
          !!formState.errors.codSettings?.confirmSetting?.total ||
          !!formState.errors.codSettings?.confirmSetting?.body?.discount ||
          !!formState.errors.codSettings?.confirmSetting?.topMessage ||
          !!formState.errors.codSettings?.confirmSetting?.total?.donate
            ?.label ||
          (!hasConfirmationDonationLabel && props.hasTriedSaving),
        content: <div></div>,
      },
      {
        label: lang.codKnownUserMessaging,
        onLabelClick: onToggleKnownUserDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError:
          !!formState.errors.codSettings?.confirmSetting?.footer?.knownCustomer,
        content: <div></div>,
      },
      {
        label: lang.codUnknownUserMessaging,
        onLabelClick: onToggleUnknownUserDrawer,
        chevronIcon: <ArrowForwardIcon />,
        disabledExpand: true,
        hasError:
          !!formState.errors.codSettings?.confirmSetting?.footer
            ?.unknownCustomer,
        content: <div></div>,
      },
    ],
  };

  const backgroundShades = [
    {
      value: BackgroundColor.BLACK,
      shadeValue: BackgroundColor.BLACK,
      colorTitle: 'Promo black',
      colorValue: '#000000 to #434040',
    },
    {
      value: BackgroundColor.GOLD,
      shadeValue: BackgroundColor.GOLD,
      colorTitle: 'Promo gold',
      colorValue: '#FFBC0D to #FFDF1E',
    },
  ];

  const dividerShades = [
    {
      value: `4px solid ${DividerColor.WHITE}`,
      shadeValue: DividerColor.WHITE,
      colorTitle: 'White',
      colorValue: DividerColor.WHITE as string,
    },
    {
      value: `4px solid ${DividerColor.YELLOW}`,
      shadeValue: DividerColor.YELLOW,
      colorTitle: 'Yellow',
      colorValue: DividerColor.YELLOW as string,
    },
  ];

  return (
    <FormWrapper formState={formState} onSave={submitWrapper(handleSubmit)}>
      <TabContext value={selectedTab}>
        <CustomTabList
          onChange={tabChange}
          aria-label="Customer Order and Order Confirmation Tabs">
          <CustomTab
            label={lang.codCustomerOrderTabHeader}
            value={TAB_VALUES.COD}
          />
          <CustomTab
            label={lang.codOrderConfirmationTab}
            value={TAB_VALUES.OCD}
          />
        </CustomTabList>
        <CustomTabPanel value={TAB_VALUES.COD}>
          <SidebarAccordion items={items.customerOrder} />
        </CustomTabPanel>
        <CustomTabPanel value={TAB_VALUES.OCD}>
          <SidebarAccordion items={items.orderConfirmation} />
        </CustomTabPanel>
      </TabContext>

      {openBackgroundDrawer && (
        <Drawer open={openBackgroundDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.codColorDrawer}
              onBackClick={onToggleBackgroundDrawer}
            />
            <SidebarContent>
              <Grid container spacing={1} padding={3}>
                <ColorButtonGroup
                  label={lang.backgroundColor}
                  value={get(
                    props.componentDetails,
                    'config.codSettings.globalSetting.background',
                  )}
                  onChange={(nextValue: string) =>
                    onBackgroundColorChange(nextValue, handleOnChange)
                  }
                  colorOptions={backgroundShades}
                />
                <ColorButtonGroup
                  label={lang.dividerColor}
                  value={
                    props.componentDetails.config?.codSettings?.codSetting
                      ?.header?.order?.border
                  }
                  onChange={(nextValue: string) =>
                    handleOnChange(
                      'codSettings.codSetting.header.order.border',
                      nextValue,
                    )
                  }
                  colorOptions={dividerShades}
                />
              </Grid>
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}

      {openTitleDrawer && (
        <Drawer open={openTitleDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.headerTextAndStyling}
              onBackClick={onToggleTitleDrawer}
            />
            <SidebarContent>
              <TitleAccordion
                codSetting={get(
                  props.componentDetails,
                  'config.codSettings.codSetting',
                )}
                showCodSettingRmhcMessage={showCodSettingRmhcMessage}
                errors={formState.errors?.codSettings?.codSetting}
                showValidationErrors={props.hasTriedSaving}
                handleOnChange={handleOnChange}
                theme={props.componentDetails.theme}
              />
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}

      {openCustomMessageDrawer && (
        <Drawer open={openCustomMessageDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.codCustomMessaging}
              onBackClick={onCustomMessageDrawer}
            />
            <SidebarContent>
              <MessagingAccordion
                footer={get(
                  props.componentDetails,
                  'config.codSettings.codSetting.footer',
                )}
                errors={formState.errors?.codSettings?.codSetting?.footer}
                handleChange={handleOnChange}
                selectedCustomer={selectedCustomer}
                paymentEventType={paymentEventType}
                handlePaymentEventType={handleChangePaymentEventType}
                handleSelectedCustomer={handleSelectedCustomer}
                theme={props.componentDetails.theme}
              />
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}

      <Drawer open={openComponentSettingsDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.componentSettings}
            onBackClick={onComponentSettingsDrawer}
          />
          <SidebarContent>
            <CustomCodSettings
              globalSetting={
                props.componentDetails.config?.codSettings?.globalSetting
              }
              errors={formState.errors?.codSettings?.globalSetting}
              onChange={handleOnChange}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      {openHeaderSectionDrawer && (
        <Drawer open={openHeaderSectionDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.codHeaderSection}
              onBackClick={onHeaderSectionDrawer}
            />
            <SidebarContent>
              <Grid item px={2} mt={2} xs={12}>
                <SwitchButtonWidget
                  value={showHeaderSection}
                  onChange={(value) =>
                    handleOnChange(
                      'codSettings.confirmSetting.header.review.banner.active',
                      value,
                    )
                  }
                />
              </Grid>
              <HeaderSection
                header={get(
                  props.componentDetails,
                  'config.codSettings.confirmSetting.header',
                )}
                errors={formState.errors?.codSettings?.confirmSetting?.header}
                showValidationErrors={props.hasTriedSaving}
                handleOnChange={handleOnChange}
                setOrderType={props.setOrderType}
                hasCodFullBanner={hasCodFullBanner}
                hasCodHalfBanner={hasCodHalfBanner}
              />
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}

      {openConfirmTitleDrawer && (
        <Drawer open={openConfirmTitleDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.headerTextAndStyling}
              onBackClick={onToggleConfirmTitleDrawer}
            />
            <SidebarContent>
              <OrderConfirmTitle
                confirmSetting={get(
                  props.componentDetails,
                  'config.codSettings.confirmSetting',
                )}
                errors={formState.errors?.codSettings?.confirmSetting}
                showValidationErrors={props.hasTriedSaving}
                handleOnChange={handleOnChange}
                theme={props.componentDetails.theme}
              />
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}

      {openKnownUserDrawer && (
        <Drawer open={openKnownUserDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.codKnownUserMessaging}
              rightComponent={
                <EyeIconButton
                  isHighlighted={selectedCustomer === CustomerTypes.KNOWN}
                  onClick={handleSelectedCustomer(CustomerTypes.KNOWN)}
                />
              }
              onBackClick={onToggleKnownUserDrawer}
            />
            <SidebarContent>
              <ButtonContainer>
                <PaymentTypeToggleButton
                  paymentEventType={paymentEventType}
                  onTogglePaymentEventType={handleChangePaymentEventType}
                />
              </ButtonContainer>
              <ConfirmOrderMessages
                messages={get(
                  props.componentDetails,
                  `config.codSettings.confirmSetting.footer.knownCustomer.${paymentEventType}`,
                  [],
                )}
                messageTemplate={generateCODMessage(selectedCustomer)}
                errors={
                  formState.errors?.codSettings?.codSetting?.footer
                    ?.knownCustomer?.[paymentEventType] as FieldErrors<
                    IMessageConfig[]
                  >
                }
                onChange={(value) =>
                  handleOnChange(
                    `codSettings.confirmSetting.footer.knownCustomer.${paymentEventType}`,
                    value,
                  )
                }
                theme={props.componentDetails.theme}
              />
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}

      {openUnknownUserDrawer && (
        <Drawer open={openUnknownUserDrawer}>
          <SidebarContainer>
            <SidebarHeader
              label={lang.codUnknownUserMessaging}
              rightComponent={
                <EyeIconButton
                  isHighlighted={selectedCustomer === CustomerTypes.UNKNOWN}
                  onClick={handleSelectedCustomer(CustomerTypes.UNKNOWN)}
                />
              }
              onBackClick={onToggleUnknownUserDrawer}
            />
            <SidebarContent>
              <ConfirmOrderMessages
                messages={get(
                  props.componentDetails,
                  'config.codSettings.confirmSetting.footer.unknownCustomer',
                  [],
                )}
                messageTemplate={generateCODMessage(selectedCustomer)}
                errors={
                  formState.errors?.codSettings?.confirmSetting?.footer
                    ?.unknownCustomer as FieldErrors<IMessageConfig[]>
                }
                onChange={(value) =>
                  handleOnChange(
                    `codSettings.confirmSetting.footer.unknownCustomer`,
                    value,
                  )
                }
                theme={props.componentDetails.theme}
              />
            </SidebarContent>
          </SidebarContainer>
        </Drawer>
      )}
    </FormWrapper>
  );
};
